// EventsFiltersMenu – portal popover that replaces the old events filters
// sidebar. Hosts the full EventsFiltersPanel behind a single toolbar button.

import React, { JSX, useEffect, useLayoutEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import type { EventFiltersValue } from '../types/EventsOverviewTypes';
import EventsFiltersPanel from './EventFilterPanel';

const MENU_WIDTH = 320;

interface EventsFiltersMenuProps {
  open: boolean;
  onClose: () => void;
  anchorRef: React.RefObject<HTMLButtonElement | null>;
  filters: EventFiltersValue;
  onChangeFilters: (next: EventFiltersValue) => void;
}

export function EventsFiltersMenu(props: EventsFiltersMenuProps): JSX.Element | null {
  const { open, onClose, anchorRef, filters, onChangeFilters } = props;

  const [coords, setCoords] = useState({ top: 0, left: 0, width: MENU_WIDTH, maxHeight: 480 });

  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent): void {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  useLayoutEffect(() => {
    if (!open) return;

    function updatePosition(): void {
      const el = anchorRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      const width = Math.min(MENU_WIDTH, window.innerWidth - 24);
      let left = r.right - width;
      left = Math.max(8, Math.min(left, window.innerWidth - width - 8));
      const top = r.bottom + 6;
      const maxHeight = Math.max(240, window.innerHeight - top - 12);
      setCoords({ top, left, width, maxHeight });
    }

    updatePosition();
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);
    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [open, anchorRef]);

  if (!open || typeof document === 'undefined') return null;

  return createPortal(
    <>
      <div className="fixed inset-0 z-[280] bg-slate-900/25 dark:bg-black/50" role="presentation" onClick={onClose} />
      <div
        className="fixed z-[290] flex flex-col overflow-hidden rounded-xl border border-gray-200 bg-white text-left text-gray-800 shadow-2xl ring-1 ring-black/5 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100"
        style={{ top: coords.top, left: coords.left, width: coords.width, maxHeight: coords.maxHeight }}
        role="dialog"
        aria-modal="true"
        aria-label="Event filters"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex shrink-0 items-center justify-between border-b border-gray-100 dark:border-slate-700 bg-gray-50 dark:bg-slate-800 px-3 py-2">
          <div className="text-xs font-semibold text-gray-800 dark:text-slate-200">Filters</div>
          <button
            type="button"
            className="rounded-md p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-700 dark:text-slate-400 dark:hover:bg-slate-700 dark:hover:text-slate-200"
            onClick={onClose}
            aria-label="Close filters"
            title="Close"
          >
            <X className="h-4 w-4" aria-hidden />
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-auto p-3">
          <EventsFiltersPanel value={filters} onChange={onChangeFilters} />
        </div>
      </div>
    </>,
    document.body,
  );
}
